import {useCallback, useMemo} from 'react';
import moment from 'moment';

import type {SkillPathSettingsFilterInput} from 'src/gql/types';

import {useAllAdaptiveSubjects} from 'client/TeacherDashboard/Home/hooks/useHomeAdaptiveSubjects';

import {useTeacherSkillPathsQuery} from './SkillPaths.queries.generated';
import type {TeacherSkillPathsQuery} from './SkillPaths.queries.generated';
import type {
  DueWithinFilter,
  SkillPathRowData,
  SkillPathSortValue,
  SkillPathStatusSegment,
  SkillPathSubjectOption
} from './types';

interface Params {
  classroomId?: string;
  segment: SkillPathStatusSegment;
  search: string;
  subject: string;
  dueWithin: DueWithinFilter;
  dueDateStart: string;
  dueDateEnd: string;
  sort: {field: SkillPathSortValue; order: 'asc' | 'desc'};
}

interface Result {
  rows: SkillPathRowData[];
  counts: Record<SkillPathStatusSegment, number>;
  subjectOptions: SkillPathSubjectOption[];
  isLoading: boolean;
  hasError: boolean;
  /** No skill paths anywhere in this scope (drives the onboarding empty state). */
  isScopeEmpty: boolean;
  refetch: () => void;
}

type SettingsRow = NonNullable<
  NonNullable<TeacherSkillPathsQuery['skillPathSettings']>['results']
>[number];

// INTERIM (client stand-in for missing BE support) — the status/roll-up helpers below and the
// filter/sort/counts in the returned memo all belong server-side. End state mirrors TeacherAssignments
// (fully server-driven): a per-skill `skillPathsAsEducator`-style query returning one row per skill with
// scope-aware status, dates, and completion, plus server filter/sort/keyset pagination. When that lands,
// delete these helpers and pass the UI state as query variables instead.

// One skill's status is the "best" across its recipient-group rows (open > scheduled > closed),
// mirroring the assignment list's open-if-any-in-scope semantics.
const rowStatus = (
  startDate: string | null | undefined,
  dueDate: string,
  now: moment.Moment
): SkillPathStatusSegment => {
  if (startDate && moment(startDate).isAfter(now)) {
    return 'scheduled';
  }
  if (moment(dueDate).isBefore(now)) {
    return 'closed';
  }
  return 'open';
};

// interim: groups settings rows → one row per skill + aggregates status/dates/recipients (the server's job).
const rollUpSkill = (
  guideLevelId: string,
  settingsRows: SettingsRow[],
  classroomId: string | undefined,
  now: moment.Moment
): SkillPathRowData => {
  const {guideLevel} = settingsRows[0];
  const studentIds = new Set<string>();
  // Per-group status drives both the roll-up status AND which groups' dates feed the aggregates.
  const rowStatuses = settingsRows.map((row) => rowStatus(row.startDate, row.dueDate, now));

  settingsRows.forEach((row) => {
    row.studentSkillPaths.forEach((path) => {
      if (!classroomId || path.classroomId === classroomId) {
        studentIds.add(path.studentId);
      }
    });
  });

  const status: SkillPathStatusSegment = rowStatuses.includes('open')
    ? 'open'
    : rowStatuses.includes('scheduled')
      ? 'scheduled'
      : 'closed';

  // Scope date aggregates to the groups matching the roll-up status: a closed group's past due/start
  // date must not skew an open/scheduled roll-up's window filter, dueDate sort key, or "Starts …" copy.
  // Fall back to all groups only if none match — unreachable, since status is drawn from rowStatuses.
  const statusRows = settingsRows.filter((_row, i) => rowStatuses[i] === status);
  const scopedRows = statusRows.length > 0 ? statusRows : settingsRows;
  const sortedDue = scopedRows.map((row) => row.dueDate).sort();
  const sortedStart = scopedRows.flatMap((row) => (row.startDate ? [row.startDate] : [])).sort();

  return {
    id: guideLevelId,
    skillName: guideLevel.name,
    code: guideLevel.code ?? '',
    guideId: guideLevel.guideId,
    subjectId: guideLevel.subjectId,
    themeId: guideLevel.parent?.parent?.id,
    topicId: guideLevel.parent?.id,
    onStudentsSkillPaths: studentIds.size > 0,
    status,
    minStartDate: sortedStart[0] ?? null,
    maxStartDate: sortedStart[sortedStart.length - 1] ?? null,
    minDueDate: sortedDue[0],
    maxDueDate: sortedDue[sortedDue.length - 1]
  };
};

// interim: client-side due-date filtering (→ server `filter` arg).
const dueWithinBounds = (
  dueWithin: DueWithinFilter,
  customStart: string,
  customEnd: string
): {start: moment.Moment | null; end: moment.Moment | null} => {
  switch (dueWithin) {
    case 'week':
      return {start: moment().startOf('week'), end: moment().endOf('week')};
    case 'month':
      return {start: moment().startOf('month'), end: moment().endOf('month')};
    case 'custom':
      return {
        start: customStart ? moment(customStart).startOf('day') : null,
        end: customEnd ? moment(customEnd).endOf('day') : null
      };
    case 'schoolYear':
    default:
      return {start: null, end: null};
  }
};

const inDueWindow = (
  row: SkillPathRowData,
  bounds: {start: moment.Moment | null; end: moment.Moment | null}
): boolean => {
  const due = moment(row.minDueDate);
  if (bounds.start && due.isBefore(bounds.start)) {
    return false;
  }
  if (bounds.end && due.isAfter(bounds.end)) {
    return false;
  }
  return true;
};

// interim: client-side sorting (→ server `sort` arg).
const sortRows = (
  rows: SkillPathRowData[],
  sort: {field: SkillPathSortValue; order: 'asc' | 'desc'}
): SkillPathRowData[] => {
  const direction = sort.order === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (sort.field === 'name') {
      return a.skillName.localeCompare(b.skillName) * direction;
    }
    const aKey = sort.field === 'dueDate' ? a.minDueDate : (a.minStartDate ?? a.minDueDate);
    const bKey = sort.field === 'dueDate' ? b.minDueDate : (b.minStartDate ?? b.minDueDate);
    return (moment(aKey).valueOf() - moment(bKey).valueOf()) * direction;
  });
};

export const useSkillPathsList = ({
  classroomId,
  segment,
  search,
  subject,
  dueWithin,
  dueDateStart,
  dueDateEnd,
  sort
}: Params): Result => {
  // Class scope: settings rows targeting this classroom (whole-class config OR an individual there).
  const filter: SkillPathSettingsFilterInput | undefined = classroomId
    ? {
        or: [
          {classroomSkillPathConfigs: {classroomId: {eq: classroomId}}},
          {studentSkillPaths: {classroomId: {eq: classroomId}}}
        ]
      }
    : undefined;

  const {data, loading, error, refetch} = useTeacherSkillPathsQuery({
    variables: {filter},
    fetchPolicy: 'cache-and-network'
  });
  const handleRefetch = useCallback(() => {
    // Swallow the rejection: refetch rejects under errorPolicy 'none', but the failure is already
    // surfaced reactively via `error`/hasError — an uncaught reject would just be HB noise.
    void refetch().catch(() => {});
  }, [refetch]);

  // Subject filter lists the full adaptive catalog (every adaptive subject), not just the subjects the
  // teacher currently has skill paths in.
  const {subjects: adaptiveSubjects} = useAllAdaptiveSubjects();

  const allSkills = useMemo(() => {
    const results = data?.skillPathSettings?.results ?? [];
    const now = moment();
    const bySkill = new Map<string, SettingsRow[]>();
    results.forEach((row) => {
      const group = bySkill.get(row.guideLevelId);
      if (group) {
        group.push(row);
      } else {
        bySkill.set(row.guideLevelId, [row]);
      }
    });
    return Array.from(bySkill.entries()).map(([guideLevelId, rows]) =>
      rollUpSkill(guideLevelId, rows, classroomId, now)
    );
  }, [data, classroomId]);

  return useMemo(() => {
    const bounds = dueWithinBounds(dueWithin, dueDateStart, dueDateEnd);
    const normalizedSearch = search.trim().toLowerCase();

    // interim: client-side subject/search/due filtering + status counts (→ server `filter` args + the
    // per-status aliased `count` queries TeacherAssignments uses). Everything except the status segment
    // is shared by the visible list and the tab counts. NB: counts are page-local (only the <=50 rows
    // the query loads), not scope-total — server-driven counts land with pagination.
    const preSegment = allSkills.filter((row) => {
      if (subject !== 'all' && row.subjectId !== subject) {
        return false;
      }
      if (normalizedSearch && !row.skillName.toLowerCase().includes(normalizedSearch)) {
        return false;
      }
      return inDueWindow(row, bounds);
    });

    const counts = preSegment.reduce(
      (acc, row) => {
        acc[row.status] += 1;
        return acc;
      },
      {open: 0, closed: 0, scheduled: 0} as Record<SkillPathStatusSegment, number>
    );

    const rows = sortRows(
      preSegment.filter((row) => row.status === segment),
      sort
    );

    // Already sorted by name (ASC) at the query.
    const subjectOptions: SkillPathSubjectOption[] = adaptiveSubjects.map((s) => ({
      id: s.id,
      name: s.name
    }));

    return {
      rows,
      counts,
      subjectOptions,
      isLoading: loading && allSkills.length === 0,
      hasError: Boolean(error),
      isScopeEmpty: !loading && !error && allSkills.length === 0,
      refetch: handleRefetch
    };
  }, [
    allSkills,
    adaptiveSubjects,
    segment,
    search,
    subject,
    dueWithin,
    dueDateStart,
    dueDateEnd,
    sort,
    loading,
    error,
    handleRefetch
  ]);
};
